import "./App.css";
import Root from "./routes/root.jsx";
import RootShop from "./routes/rootShop.jsx";
import ErrorPage from "./pages/ErrorPage.jsx";
import store from "./store/store";
import {
  fetchStoreItemsAction,
  fetchStoreFeaturedAction,
} from "./store/shop-store";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { fetchProducts } from "./api/products";
import { getCachedProducts, setCachedProducts } from "./helpers/productsCache";
import { Helmet } from "react-helmet";
import AppSkeleton, {
  NavBarSkeleton,
} from "./components/skeletons/AppSkeleton";
import { getSkeletonForPathname } from "./helpers/routeSkeletons";
import { useEffect, Suspense } from "react";
import { scheduleTokenRefresh } from "./hooks/callRefreshToken";

export const productsLoader = async () => {
  const cachedData = getCachedProducts();
  if (cachedData) {
    store.dispatch(fetchStoreItemsAction(cachedData));
    store.dispatch(fetchStoreFeaturedAction(cachedData.slice(0, 6)));
    return cachedData;
  }

  const response = await fetchProducts();

  if (response.ok) {
    const data = await response.json();
    // Dispatch actions to update the store
    store.dispatch(fetchStoreItemsAction(data));
    // index 0 is the hero "Featured" item; indices 1-5 fill the 5-card bento grid
    store.dispatch(fetchStoreFeaturedAction(data.slice(0, 6)));
    setCachedProducts(data);
    return data; // Return the fetched data
  } else {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || "Failed to fetch products"); // Use the message from the response or a default one
  }
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <Root />,
    errorElement: <ErrorPage />,
    loader: productsLoader,
    shouldRevalidate: ({ formMethod, defaultShouldRevalidate }) =>
      formMethod ? defaultShouldRevalidate : false,
    children: [
      {
        index: true,
        lazy: () =>
          import("./pages/Home.jsx").then((module) => ({
            Component: module.default,
          })),
      },
      {
        path: "about",
        lazy: () =>
          import("./pages/About.jsx").then((module) => ({
            Component: module.default,
            loader: module.loader,
          })),
      },
      {
        path: "contact",
        lazy: () =>
          import("./pages/Contact.jsx").then((module) => ({
            Component: module.default,
            action: module.action,
          })),
      },
      {
        path: "checkout",
        lazy: () =>
          Promise.all([
            import("./pages/Checkout.jsx"),
            import("./components/Shipping.jsx"),
          ]).then(([page, shipping]) => ({
            Component: page.default,
            action: shipping.action,
          })),
      },
      {
        path: "confirmation",
        lazy: () =>
          import("./pages/Confirmation.jsx").then((module) => ({
            Component: module.default,
          })),
        id: "confirmation",
      },
      {
        path: "faq",
        lazy: () =>
          import("./pages/Faq.jsx").then((module) => ({
            Component: module.default,
            loader: module.loader,
          })),
      },
      {
        path: "favorites",
        lazy: () =>
          import("./pages/Favorites.jsx").then((module) => ({
            Component: module.default,
          })),
      },
      {
        path: "privacy-policy",
        lazy: () =>
          import("./pages/PrivacyPolicy.jsx").then((module) => ({
            Component: module.default,
          })),
      },
      {
        path: "shop",
        element: <RootShop />,
        children: [
          {
            index: true,
            lazy: () =>
              import("./pages/Shop.jsx").then((module) => ({
                Component: module.default,
                loader: module.loader,
              })),
          },
          {
            path: ":id",
            lazy: () =>
              import("./pages/ShopDetail.jsx").then((module) => ({
                Component: module.default,
                loader: module.loader,
              })),
          },
        ],
      },
    ],
  },
]);

// While App.jsx is fetching the API token/Stripe (loading, below), the
// router hasn't mounted yet, so we don't actually know which route the
// user landed on via React Router - but the URL is already sitting in the
// address bar, so pick the matching page skeleton straight from
// window.location.pathname and pair it with the fake nav bar, instead of
// always showing the generic grid (which doesn't match e.g. the FAQ
// accordion or About's photo+text layout).
const InitialLoadingSkeleton = () => {
  const { pathname } = window.location;
  const PageSkeleton = getSkeletonForPathname(pathname);

  if (!PageSkeleton) return <AppSkeleton />;

  return (
    <div className="min-h-screen">
      <NavBarSkeleton />
      <PageSkeleton />
    </div>
  );
};

function App() {
  useEffect(() => {
    scheduleTokenRefresh();
  }, []);

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Women Wavemakers</title>
        <meta
          name="description"
          content="An eCommerce site selling mugs, shirts and other items for strong, active women."
        ></meta>
        <link rel="canonical" href="https:/womenwavemakers.com" />
      </Helmet>
      <Suspense fallback={<InitialLoadingSkeleton />}>
        <RouterProvider router={router} />
      </Suspense>
    </>
  );
}

export default App;
